import { useQueryClient } from '@tanstack/react-query';
import React from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { CompositeSessionInfoSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { useAllSiteSettingsMetadata } from '~src/settings/hooks/useSiteSettingsMetadata';
import { NavLocationSelectors } from '~src/spacesPlatform/selectors/NavigationStateSelectors';

import { SettingsFormikContainer } from '../../../components/common/SettingsFormikContainer';
import { GeneralSiteSettingsSubspaceState } from '../../../components/subspaces/SiteSettingsSpace/GeneralSiteSettingsSubspace';
import {
  ISiteSettingPair,
  UpdateSiteSettingsService,
} from '../../../site-settings/restApi/requests/UpdateSiteSettings';
import {
  ISiteAdminSettings,
  ISiteAdminSettingsWithSubscriptions,
} from '../../../types/ISiteSettingsForSiteAdmin';
import { filterOutServerAdminOnlySiteSettingsV2 } from '../../../utils/siteSettingsV2Filter';
import { useSelector } from '../../utils';
import { getSiteIdFromGeneralSettings } from './ReactSiteSettingsForSiteAdminSubFormik';

// Map of component section name → V2 metadata section name.
// When adding a new V2-driven section, add one entry here.
export const SECTION_TO_METADATA_NAME: ReadonlyMap<string, string> = new Map([
  ['SharingSection', 'namedSharingEnabled'],
  ['ProgressDialogSection', 'progressDialog'],
  ['CommentingSettingSection', 'commenting'],
  ['DataAlertsSection', 'dataAlertsEnabled'],
  ['RequestAccessForAISettingSection', 'requestAccessForAIEnabled'],
  ['NotificationEmailSettingsSection', 'subscriptionAndDDAEmailLinksEnabled'],
  ['CollectionsSettingSection', 'collections'],
  ['SiteGroupSetsSection', 'groupSetsEnabled'],
  ['RecycleBinSettingSection', 'recycleBin'],
  ['SemanticSearchSection', 'hybridSearchEnabled'],
  ['ContentMobilitySection', 'contentMobility'],
]);

export const v2Sections = Array.from(SECTION_TO_METADATA_NAME.keys());

export type ReactMetadataDrivenSiteSettingsSubFormikProps = {
  settings: Omit<GeneralSiteSettingsSubspaceState, 'siteAuthenticationSettings'>;
};

export function ReactMetadataDrivenSiteSettings(props: ReactMetadataDrivenSiteSettingsSubFormikProps) {
  const { settings, ...rest } = props;
  const siteId = getSiteIdFromGeneralSettings(settings);

  const submitHandler = useSubmitHandler();

  // Filter sections based on metadata access
  const filteredSections = useFilteredSections();

  // Adapter to move subscription settings down a level with the rest of settings
  // instead of on its own tier
  const siteAdminSettingsWithSubscriptions: ISiteAdminSettingsWithSubscriptions = React.useMemo(() => ({
    ...settings.siteSettings.siteAdminSettings as ISiteAdminSettings,
    subscriptionSettings: settings.siteSettings.subscriptionSettings,
  }), [settings?.siteSettings?.siteAdminSettings, settings?.siteSettings?.subscriptionSettings]);

  return (
    <SettingsFormikContainer
      formName={'ReactMetadataDrivenSiteSettings'}
      {...rest}
      settings={siteAdminSettingsWithSubscriptions}
      siteId={siteId}
      sections={filteredSections}
      onSubmit={submitHandler}
    />
  );
}

/**
 * Filter V2 sections to those the current user can access.
 *
 * For Server/Site Admins: all sections are included.
 * For Admin Profile users: a section is included iff the V2 metadata API
 * returned a sectionMetadata entry with that section's metadata name
 * (which itself reflects the server-side modifiableWith capability check).
 *
 * While the metadata call is in flight we return an empty list so we don't
 * flash sections that may ultimately be filtered out.
 */
export function useFilteredSections(): string[] {
  const isSiteOrServerAdmin = useSelector(
    CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector,
  );
  const { allMetadata, isLoading } = useAllSiteSettingsMetadata();

  return React.useMemo(() => {
    if (isSiteOrServerAdmin) {
      return v2Sections;
    }
    if (!FeatureFlags.metadataDrivenSiteSettingsEnabled) {
      return [];
    }
    if (isLoading || !allMetadata) {
      return [];
    }
    const visibleMetadataNames = new Set(allMetadata.sectionMetadata.map(s => s.name));
    return v2Sections.filter(section => {
      const metadataName = SECTION_TO_METADATA_NAME.get(section);
      // If the section isn't mapped, include it by default (future-proof).
      return metadataName === undefined || visibleMetadataNames.has(metadataName);
    });
  }, [isSiteOrServerAdmin, allMetadata, isLoading]);
}

function useSubmitHandler() {
  const isInSettingsLocation = useSelector(NavLocationSelectors.inSettingsLocation);
  const queryClient = useQueryClient();
  const { allMetadata } = useAllSiteSettingsMetadata();

  return React.useCallback(async (newSiteAdminSettings: ISiteAdminSettingsWithSubscriptions) => {
    // Filter out server-admin-only siteSettingsV2 keys before sending
    const settingsToSend = filterOutServerAdminOnlySiteSettingsV2(
      newSiteAdminSettings as ISiteAdminSettingsWithSubscriptions & { siteSettingsV2?: Record<string, unknown> }
    ) as ISiteAdminSettingsWithSubscriptions & { siteSettingsV2?: Record<string, unknown> };

    const savedPairs = await sendSiteSettingsV2RestApiUpdate(settingsToSend.siteSettingsV2);

    // Refresh the per-section caches that MetadataDrivenSiteSetting reads from,
    // using the authoritative values the server echoed back in the PUT response.
    // This keeps the displayed value in sync after save without requiring a refetch.
    updateSiteSettingsValueCaches(queryClient, allMetadata, savedPairs);

    if (!isInSettingsLocation) return;

    return {
      settings: {
        ...settingsToSend,
        subscriptionSettings: settingsToSend.subscriptionSettings,
      },
    };
  }, [isInSettingsLocation, queryClient, allMetadata]);
}

/**
 * Send all rendered V2 site settings to the REST endpoint
 * (PUT /api/exp/sites/{siteLuid}/settings) and return the echoed-back pairs.
 * The form starts with siteSettingsV2: {} and each metadata-driven section
 * populates its values via setFieldValue as it loads, so by submit time this
 * bag contains only the settings that were actually rendered and touched.
 * The REST API requires string values: null/undefined becomes '' (clears the
 * setting server-side); booleans and numbers are coerced (e.g. true → 'true').
 */
export async function sendSiteSettingsV2RestApiUpdate(
  submittedSettings: Record<string, unknown> | undefined
): Promise<ISiteSettingPair[]> {
  const settings: ISiteSettingPair[] = Object.entries(submittedSettings ?? {})
    .map(([key, value]) => ({ key, value: value == null ? '' : String(value) }));

  if (settings.length === 0) return [];

  const response = await UpdateSiteSettingsService.updateSiteSettings(settings);
  return response.siteSetting ?? [];
}

/**
 * Group the server's echoed pairs by metadata section and write them into the
 * ['siteSettingsValues', sectionName] caches that MetadataDrivenSiteSetting
 * consumes via useQuery. Each affected section's cache is overwritten with
 * the fresh pairs; unaffected sections are untouched.
 */
export function updateSiteSettingsValueCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  allMetadata: ReturnType<typeof useAllSiteSettingsMetadata>['allMetadata'],
  savedPairs: ISiteSettingPair[],
): void {
  if (savedPairs.length === 0 || !allMetadata) return;

  // Build a reverse index: setting name → metadata section name.
  const settingToSection = new Map<string, string>();
  for (const section of allMetadata.sectionMetadata) {
    for (const meta of section.siteSettingMetadata) {
      settingToSection.set(meta.name, section.name);
    }
  }

  // Group saved pairs by the section they belong to.
  const pairsBySection = new Map<string, ISiteSettingPair[]>();
  for (const pair of savedPairs) {
    const section = settingToSection.get(pair.key);
    if (!section) continue;
    const bucket = pairsBySection.get(section);
    if (bucket) {
      bucket.push(pair);
    } else {
      pairsBySection.set(section, [pair]);
    }
  }

  pairsBySection.forEach((pairs, sectionName) => {
    queryClient.setQueryData<ISiteSettingPair[]>(
      ['siteSettingsValues', sectionName],
      (prev) => {
        const updatedKeys = new Set(pairs.map(p => p.key));
        const untouched = (prev ?? []).filter((p: ISiteSettingPair) => !updatedKeys.has(p.key));
        return untouched.concat(pairs);
      },
    );
  });
}
